import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import { LoadFailureNotice } from '../ui/LoadFailureNotice';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { formatCalendarDate, formatInstant } from '../../lib/calendar-date';
import { fmtCurrency } from '../../lib/display-labels';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { snapshotReasonLabel, type CustomerSnapshot, type Figure } from '../../types/customer-snapshot';

/**
 * The Account snapshot on the customer page (CRM-FE-4): GET
 * /customers/:id/snapshot, VERBATIM.
 *
 * A null figure renders "—" with the API's reason under it, never 0; a real 0
 * renders as 0. AR comes from the API's one receivables definition
 * (lib/invoice-overdue.ts) and is shown to sales here only (operator,
 * 2026-10-13). Nothing on this panel is added, subtracted or re-dated in the
 * browser — Scout will cite these exact figures, so a figure that looks wrong
 * is an API fix, not a screen fix.
 */
function Cell({ label, figure, render }: { label: string; figure: Figure<unknown>; render: (v: any) => React.ReactNode }) {
  const absent = figure.value === null || figure.value === undefined;
  return (
    <div className="min-w-0" data-testid={`snapshot-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
      <dt className="text-[0.65rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">{label}</dt>
      <dd className="mt-1 text-base font-extrabold tabular-nums text-white">{absent ? '—' : render(figure.value)}</dd>
      {absent && <dd className="text-xs text-fieldiq-muted">{snapshotReasonLabel(figure.reason)}</dd>}
    </div>
  );
}

export function AccountSnapshotPanel({ customerId }: { customerId: string }) {
  const timeZone = useTenantTimeZone();
  const q = useQuery({
    queryKey: ['customer-snapshot', customerId],
    queryFn: () => api.customers.snapshot(customerId).then((r) => r.data.data as CustomerSnapshot),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failure = queryLoadFailure(q);
  const s = failure ? undefined : q.data;
  const money = (v: number) => fmtCurrency(v, s?.currency);
  const day = (v: string) => formatCalendarDate(v) ?? v;

  return (
    <section aria-labelledby="account-snapshot-title" className="rounded-xl border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 id="account-snapshot-title" className="text-xs font-bold uppercase tracking-wider text-white">Account snapshot</h3>
        {s && <p className="text-xs text-fieldiq-muted">As of {formatInstant(s.asOf, timeZone) ?? '—'}</p>}
      </div>
      {failure ? (
        <LoadFailureNotice error={queryFailureError(q)} subject="this account's snapshot" className="py-6" />
      ) : !s ? (
        <div className="mt-3 h-24 animate-pulse rounded-lg bg-fieldiq-dark/60" aria-busy="true" />
      ) : (
        <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-4 sm:grid-cols-3 xl:grid-cols-6">
          <Cell label="Last job" figure={s.work.lastJobDate} render={day} />
          <Cell label="Jobs (90 days)" figure={s.work.jobsLast90d} render={String} />
          <Cell label="Open jobs" figure={s.work.openJobs} render={String} />
          <Cell label="Last ticket" figure={s.work.lastTicketWorkDate} render={day} />
          <Cell label="Invoiced (12 months)" figure={s.money.invoicedLast12m} render={money} />
          <Cell label="AR outstanding" figure={s.money.arOutstanding} render={money} />
          <Cell label="AR overdue" figure={s.money.arOverdue} render={money} />
          <Cell
            label="Open quotes"
            figure={s.sales.openQuotes}
            render={(v: NonNullable<CustomerSnapshot['sales']['openQuotes']['value']>) => (
              <span className="flex flex-col">
                <span>{v.count}</span>
                {v.latest && (
                  <Link to={`/quotes/${v.latest.id}`} className="text-xs font-semibold text-fieldiq-muted hover:underline">
                    {[v.latest.quoteNumber ?? '—', v.latest.status, fmtCurrency(v.latest.totalAmount, v.latest.currency)].join(' · ')}
                  </Link>
                )}
              </span>
            )}
          />
          <Cell
            label="Open opportunities"
            figure={s.sales.openOpportunities}
            render={(v: NonNullable<CustomerSnapshot['sales']['openOpportunities']['value']>) => (
              <span className="flex flex-col">
                <span>{v.count}</span>
                <span className="text-xs font-semibold text-fieldiq-muted">
                  {money(v.valueKnownTotal)} known · {v.valueUnknownCount} unvalued
                </span>
              </span>
            )}
          />
          <Cell label="Last meeting" figure={s.sales.lastMeetingAt} render={(v: string) => formatInstant(v, timeZone, { month: 'short', day: 'numeric', year: 'numeric' }) ?? '—'} />
          <Cell label="Next follow-up" figure={s.sales.nextFollowUpDate} render={day} />
          <Cell label="Contacts" figure={s.contacts.count} render={String} />
        </dl>
      )}
    </section>
  );
}

export default AccountSnapshotPanel;
